import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronLeft } from 'lucide-react';
import { api, type LocationOption } from '../lib/api';
import { cacheFirst } from '../lib/cacheFirst';
import { cacheLocations, getCachedLocations } from '../lib/db';
import { CreateTicketForm } from '../components/pwa/CreateTicketForm';
import { LocationPicker } from '../components/pwa/LocationPicker';

/**
 * Chooses where the fault is, then reports it.
 *
 * A QR sticker links to /report?qr=room-302 (SPEC.md §5.1). When that resolves to a
 * single location the picker is skipped entirely — someone standing in front of a
 * broken unit should not have to find their own room in a dropdown.
 *
 * Reads the cache first and the network second (Rule 4). This screen is the entry point
 * to the whole system and it used to need a working connection to show anything at all:
 * in a plant room with no signal it rendered "Could not load locations" and the fault went
 * unreported. Now IndexedDB answers immediately — which also means the list is on screen
 * on a 3G handset seconds before the network would have replied — and the fetch that
 * follows refreshes both the screen and the cache.
 */
export function ReportPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const qrCode = params.get('qr') ?? undefined;

  const [locations, setLocations] = useState<LocationOption[] | null>(null);
  const [selectedId, setSelectedId] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  useEffect(
    () =>
      cacheFirst<LocationOption[]>({
        // An empty cache is "never fetched", not "no places" — the picker has its own
        // empty state for a property with nothing in it, and only a real fetch can say so.
        readCache: async () => {
          const rows = await getCachedLocations(qrCode);
          return rows.length > 0 ? rows : null;
        },
        load: () => api.listLocations(qrCode),
        writeCache: (rows) => cacheLocations(rows, qrCode === undefined),
        onValue: (rows, source) => {
          setLocations(rows);
          // One match means the QR told us exactly where we are. Auto-selecting from the
          // cache is only safe with a qrCode: without one the cache may hold a subset from
          // an earlier sticker visit, and picking from that would drop someone into the
          // form for a place they never chose.
          const authoritative = source === 'network' || qrCode !== undefined;
          if (authoritative && rows.length === 1) setSelectedId(rows[0]!.id);
        },
        onError: () => setError(t('report.locationsFailed')),
      }),
    [qrCode, t],
  );

  if (error)
    return (
      <p role="alert" className="p-4 text-sm text-red-400">
        {error}
      </p>
    );
  if (!locations) return <p className="p-4 text-sm text-slate-400">{t('report.loading')}</p>;

  if (locations.length === 0) {
    return <p className="p-4 text-sm text-slate-500">{t('report.noLocations')}</p>;
  }

  const selected = locations.find((location) => location.id === selectedId);

  if (!selected) {
    return (
      <div className="p-4">
        <button
          type="button"
          onClick={() => navigate('/')}
          className="-ml-2 mb-2 flex min-h-[44px] w-fit items-center gap-1 bg-transparent px-2 text-sm text-brookfields-gold-light"
        >
          <ChevronLeft className="size-4" aria-hidden="true" />
          {t('report.backToJobs')}
        </button>
        <h1 className="text-lg font-semibold">{t('report.whereHeading')}</h1>
        <LocationPicker places={locations} onSelect={(place) => setSelectedId(place.id)} />
      </div>
    );
  }

  return (
    <CreateTicketForm
      locationId={selected.id}
      locationName={selected.name}
      onCreated={(ticketId) => navigate(`/tickets/${ticketId}`)}
      // Back returns to the picker, which is one step, not a page. Offered only when
      // there was a choice to make: a scanned QR resolves to one place, and sending
      // someone back to a list of one would be a step that asks nothing.
      onBack={qrCode === undefined && locations.length > 1 ? () => setSelectedId('') : undefined}
      onCancel={() => navigate('/')}
    />
  );
}
